import React from 'react';
import {shallowEqual} from 'react-redux';
import {ItemProps} from 'react-virtuoso';

import getTestProps from 'test/lib/cypress/getTestProps';

type TableRowTagProps = Omit<ItemProps<unknown>, 'item'> & {item?: unknown; context?: unknown};

/**
 * In order to support optimized rendering of a TableRow, the <tr> tag must be memoized and have a stable
 * value.
 *
 * So this is a pure component that just renders the <tr> tag, and it is memoized so that it only re-renders
 * when the children or the props change.
 *
 * Virtuoso always supplies `item` (the row data) and `context` to its row component, but this wrapper
 * renders no cell data, so both are dropped rather than rendered or compared.
 */
export default React.memo(
  (props: TableRowTagProps): JSX.Element => {
    const index = props['data-index'];
    const {children, item: _item, context: _context, ...rest} = props;
    return (
      <tr
        className="mediocre-tableEditable-tableRow"
        key={index}
        {...rest}
        {...getTestProps({component: 'TableRow', element: 'root', index: index.toString()})}
      >
        {children}
      </tr>
    );
  },
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    Object.keys(nextProps).every(
      // `item` and `context` are not rendered here, so they are skipped.  The children cells are memoized
      // and decide for themselves when to update.
      (key) => key === 'item' || key === 'context' || shallowEqual(prevProps[key], nextProps[key])
    )
);
